import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAuth } from '../context/AuthContext';

const { FiSearch, FiMenu, FiX, FiMapPin, FiStar, FiUser, FiLogOut } = FiIcons;

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();

  const handleSearch = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const searchTerm = formData.get('search');
    const location = formData.get('location');
    navigate(`/search?q=${encodeURIComponent(searchTerm)}&location=${encodeURIComponent(location)}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="bg-white shadow-lg relative z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiStar} className="text-white text-xl" />
            </div>
            <span className="text-2xl font-bold text-gray-900">ServiceList</span>
          </Link>

          {/* Desktop Search - Always visible */}
          <div className="hidden md:flex flex-1 max-w-2xl mx-8">
            <form onSubmit={handleSearch} className="w-full">
              <div className="flex bg-gray-100 rounded-lg overflow-hidden">
                <div className="flex-1 flex items-center px-4">
                  <SafeIcon icon={FiSearch} className="text-gray-400 mr-3" />
                  <input
                    type="text"
                    name="search"
                    placeholder="What service do you need?"
                    className="w-full bg-transparent py-3 text-gray-700 placeholder-gray-500 focus:outline-none"
                  />
                </div>
                <div className="flex items-center px-4 border-l border-gray-300">
                  <SafeIcon icon={FiMapPin} className="text-gray-400 mr-3" />
                  <input
                    type="text"
                    name="location"
                    placeholder="Enter ZIP code"
                    className="w-32 bg-transparent py-3 text-gray-700 placeholder-gray-500 focus:outline-none"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-primary-600 text-white px-6 py-3 hover:bg-primary-700 transition-colors"
                >
                  Search
                </button>
              </div>
            </form>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link
              to="/categories"
              className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
            >
              Categories
            </Link>
            
            {/* Pro Dashboard - Always visible */}
            <Link
              to="/pro/dashboard"
              className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
            >
              Pro Dashboard
            </Link>

            {isAuthenticated ? (
              <>
                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 transition-colors"
                  >
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiUser} className="text-primary-600" />
                    </div>
                  </button>

                  {isUserMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <SafeIcon icon={FiLogOut} className="mr-2" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <Link
                to="/join"
                className="bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                Join as Pro
              </Link>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100"
          >
            <SafeIcon icon={isMenuOpen ? FiX : FiMenu} className="text-xl" />
          </button>
        </div>

        {/* Mobile Search - Always visible */}
        <div className="md:hidden pb-4">
          <form onSubmit={handleSearch}>
            <div className="space-y-2">
              <div className="flex items-center bg-gray-100 rounded-lg px-4 py-3">
                <SafeIcon icon={FiSearch} className="text-gray-400 mr-3" />
                <input
                  type="text"
                  name="search"
                  placeholder="What service do you need?"
                  className="w-full bg-transparent text-gray-700 placeholder-gray-500 focus:outline-none"
                />
              </div>
              <div className="flex space-x-2">
                <div className="flex-1 flex items-center bg-gray-100 rounded-lg px-4 py-3">
                  <SafeIcon icon={FiMapPin} className="text-gray-400 mr-3" />
                  <input
                    type="text"
                    name="location"
                    placeholder="Enter ZIP code"
                    className="w-full bg-transparent text-gray-700 placeholder-gray-500 focus:outline-none"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Search
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="md:hidden bg-white border-t border-gray-200"
        >
          <nav className="px-4 py-4 space-y-2">
            <Link
              to="/categories"
              className="block py-2 text-gray-700 hover:text-primary-600 font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Categories
            </Link>
            
            {/* Pro Dashboard - Always visible on mobile */}
            <Link
              to="/pro/dashboard"
              className="block py-2 text-gray-700 hover:text-primary-600 font-medium"
              onClick={() => setIsMenuOpen(false)}
            >
              Pro Dashboard
            </Link>

            {isAuthenticated ? (
              <>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left py-2 text-gray-700 hover:text-primary-600 font-medium"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <Link
                to="/join"
                className="block py-2 text-primary-600 hover:text-primary-700 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Join as Pro
              </Link>
            )}
          </nav>
        </motion.div>
      )}
    </header>
  );
};

export default Header;